import React, { useCallback, useEffect, useState } from 'react';
import { Send, X } from 'lucide-react';
import {
  capacityLimitsService, LimitSyncRow, PushOutcome, PushPreview, PushResultStatus,
} from 'api/services/capacityLimits.service';

const TH = 'px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const TD = 'px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 align-top';
const dash = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v);
const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : '—');

export const SYNC_OFF_COPY =
  'Push to switches is OFF (voiceflow.sync.enabled=false). Nothing can be sent until it is turned on in the backend config.';

const resultTone = (s: PushResultStatus): string => ({
  VERIFIED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  NO_TARGET: 'bg-amber-100 text-amber-800',
  NOT_TRANSLATABLE: 'bg-amber-100 text-amber-800',
  UNVERIFIABLE: 'bg-amber-100 text-amber-800',
  CPASS_ONLY: 'bg-gray-200 text-gray-700',
  SKIPPED: 'bg-gray-200 text-gray-700',
} as Record<PushResultStatus, string>)[s] || 'bg-gray-200 text-gray-700';

const failText = (res: { error?: string; message?: string }, fallback: string) => res.error || res.message || fallback;

/**
 * Master "Push to switch" for one capacity limit: preview what each switch would get (and
 * what is withheld, e.g. channels until the switch's counters are safe), apply, then show
 * the read-back per switch and the stored sync status.
 */
const PushToSwitchModal: React.FC<{ limitId: number; title: string; onClose: () => void; onPushed?: () => void }> =
  ({ limitId, title, onClose, onPushed }) => {
    const [preview, setPreview] = useState<PushPreview | null>(null);
    const [outcome, setOutcome] = useState<PushOutcome | null>(null);
    const [sync, setSync] = useState<LimitSyncRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const loadSync = useCallback(async () => {
      const res = await capacityLimitsService.limitSync(limitId);
      setSync(res.success ? res.data || [] : []);
    }, [limitId]);

    useEffect(() => {
      let live = true;
      capacityLimitsService.pushPreview(limitId).then(res => {
        if (!live) return;
        if (res.success) setPreview(res.data || null); else setError(failText(res, 'Failed to preview the push'));
      });
      loadSync();
      return () => { live = false; };
    }, [limitId, loadSync]);

    const pushable = !!preview && preview.targets.some(t => t.action === 'PUSH');
    const canApply = !!preview && preview.syncEnabled && pushable && !busy;

    const apply = async () => {
      setBusy(true); setError(null);
      const res = await capacityLimitsService.push(limitId);
      if (res.success && res.data) {
        setOutcome(res.data);
        if (res.data.sync) setSync(res.data.sync);
        onPushed?.();
      } else {
        setError(failText(res, 'Push failed'));
      }
      await loadSync();
      setBusy(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Send className="w-5 h-5" /> Push to switch — {title}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X className="w-5 h-5" /></button>
          </div>
          <div className="px-6 py-4 overflow-y-auto space-y-4">
            {error && <div data-testid="push-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
            {!preview && !error && <p className="text-sm text-gray-500">Working out what each switch would get…</p>}

            {preview && (
              <section>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Preview</h3>
                {!preview.syncEnabled && (
                  <div data-testid="sync-off" className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2 mb-2">{SYNC_OFF_COPY}</div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="preview-table">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>{['Switch', 'Action', 'Key(s)', 'CPS', 'Channels', 'Why'].map(h => <th key={h} className={TH}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {preview.targets.length === 0 && <tr><td colSpan={6} className={`${TD} text-center text-gray-500`}>No switch carries this limit.</td></tr>}
                      {preview.targets.map((t, i) => (
                        <tr key={`${t.switchProviderId}-${i}`}>
                          <td className={TD}>{t.switchName || '—'}</td>
                          <td className={TD}>
                            <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${t.action === 'PUSH' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700'}`}>
                              {t.action === 'PUSH' ? 'push' : `skip (${t.kind.replace(/_/g, ' ').toLowerCase()})`}
                            </span>
                          </td>
                          <td className={`${TD} font-mono`}>
                            {t.dimensions.slice(0, 4).map(d => <div key={`${d.type}=${d.value}`}>{d.type}={d.value}</div>)}
                            {t.dimensions.length > 4 && <div className="text-gray-400">+{t.dimensions.length - 4} more</div>}
                            {t.dimensions.length === 0 && '—'}
                          </td>
                          <td className={TD}>{t.action === 'PUSH' ? dash(t.cps) : '—'}</td>
                          <td className={TD}>
                            {t.action === 'PUSH' ? dash(t.concurrency) : '—'}
                            {t.concurrencyWithheldReason && (
                              <div className="text-amber-700 dark:text-amber-400">withheld: {t.concurrencyWithheldReason}</div>
                            )}
                          </td>
                          <td className={TD}>{t.reason || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={apply} disabled={!canApply}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm inline-flex items-center gap-1 disabled:opacity-50">
                    <Send className="w-4 h-4" /> {busy ? 'Pushing…' : 'Apply'}
                  </button>
                  <span className="text-xs text-gray-500" data-testid="apply-hint">
                    {!preview.syncEnabled ? 'Disabled: push to switches is off.'
                      : !pushable ? 'Nothing to push: no switch can take this limit.'
                        : 'Writes the row on each switch above, refreshes its limiter, then reads it back.'}
                  </span>
                </div>
              </section>
            )}

            {outcome && (
              <section>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Read-back</h3>
                <table className="w-full" data-testid="results-table">
                  <thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{['Switch', 'Result', 'Key(s)', 'Error'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {outcome.results.map((r, i) => (
                      <tr key={`${r.switchProviderId}-${i}`}>
                        <td className={TD}>{r.switchName || '—'}</td>
                        <td className={TD}><span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${resultTone(r.status)}`}>{r.status.replace(/_/g, ' ')}</span></td>
                        <td className={`${TD} font-mono`}>{r.dimensions.map(d => `${d.type}=${d.value}`).slice(0, 2).join(', ') || '—'}</td>
                        <td className={TD}>{r.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <section>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Sync status</h3>
              {sync === null && <p className="text-xs text-gray-500">Loading…</p>}
              {sync && sync.length === 0 && <p className="text-xs text-gray-500">Nothing recorded for this limit yet.</p>}
              {sync && sync.length > 0 && (
                <table className="w-full" data-testid="sync-table">
                  <thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{['Switch', 'Key', 'Check', 'Push', 'Pushed', 'Verified'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sync.map(r => (
                      <tr key={`${r.switch_key || r.switch_name}-${r.dimension_key}`}>
                        <td className={TD}>{r.switch_name || '—'}</td>
                        <td className={`${TD} font-mono`}>{r.dimension_key}</td>
                        <td className={TD}>{r.check_status || '—'}</td>
                        <td className={TD}>{r.push_status || '—'}{r.push_error ? <div className="text-red-700">{r.push_error}</div> : null}</td>
                        <td className={TD}>{when(r.last_pushed_at)}</td>
                        <td className={TD}>{when(r.last_verified_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </div>
      </div>
    );
  };

export default PushToSwitchModal;
